import { type SubjectTypesType, type QueryFactoryType, getQueryFactory } from '../lib/queryFactory'
import { getAllFilters } from './filters'
import { blockTypeToSubjectType, SUBJECT_TYPES } from '@aytm/app/insights/dashboard/lib/config'

import type { ItemType, SubjectType, TrackTargetType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import type { FiltersOrderedMap } from '@aytm/app/insights/dashboard/models/types.ClientModel'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import { BlockModel } from '@aytm/app/insights/dashboard/models/BlockModel'
import { subjectDataMapperFactory } from './editor'
import staticStore from '@aytm/app/insights/dashboard/stores/static.store'
import { presetWaveUids } from './items'

const SYNC_REQUEST_TYPES = ['dataItems', 'statItems', 'update'] as string[]

export type UpdateOptions = {
  type: string
  action: string
  data: object | string | number
}

type RequestOptions = {
  type: 'export' | 'update' | 'schema' | 'dataHashes' | 'dataItems' | 'statHashes' | 'statItems'
  blockUID: string
  subjectType?: SubjectTypesType
  refresh?: boolean
  sync?: boolean
  preset?: string
  group?: string
  split?: string
  swap?: boolean
  trackTarget?: TrackTargetType
  trackAttributes?: Record<TrackTargetType, string | null>
  confidenceLevel?: number
  secondaryConfidenceLevel?: number
  filteredRowAverages?: boolean
  items?: ItemType[]
  subjects?: Record<string, SubjectType>
  filters?: FiltersOrderedMap
  hashes?: string[]
  timestamp?: number | string | Date
  update?: UpdateOptions
  shiftKey?: boolean
  ctrlKey?: boolean
  format?: string
  metadata?: boolean
  linkToPortal?: string
}

export type AppRequestType = {
  query?: unknown
  queryHash?: string
  refresh?: boolean | 'clean' | 'noCache'
  sync?: boolean
  timestamp?: number | string | Date
}

const defaultRequestOptions: RequestOptions = {
  type: null,
  blockUID: null,
  subjectType: null,
  refresh: false,
  sync: false,
  preset: 'All',
  group: null,
  split: null,
  swap: null,
  trackTarget: 'entity',
  trackAttributes: { attribute: null, entity: null },
  confidenceLevel: null,
  secondaryConfidenceLevel: null,
  filteredRowAverages: false,
  items: null,
  subjects: null,
  filters: null,
  timestamp: null
}

const getSubjectUpdatePayload = (state: InsightsDashboardPoolState) => {
  const editorType = state?.editor?.type
  const editorData = state?.editor?.data
  const editorAction = editorType ? ('delete' === editorType ? 'delete' : 'save') : null
  if (editorData) {
    if ('save' === editorAction) {
      const queryHash = state?.currentData?.queryHash || null
      const attributes = state?.currentData?.attributes || {}
      return {
        type: 'subject',
        action: 'save',
        data: subjectDataMapperFactory(attributes, 'entity')(editorData),
        queryHash
      }
    } else if ('delete' === editorAction) {
      return {
        type: 'subject',
        action: 'delete',
        data: { uid: editorData.uid, nativeUid: editorData.nativeUid, hash: editorData.hash }
      }
    }
  }
  return null
}

export const getRequest = (
  requestOptions: RequestOptions = defaultRequestOptions,
  state: InsightsDashboardPoolState = null
): AppRequestType => {
  const {
    type,
    subjectType,
    blockUID,
    refresh,
    sync,
    preset,
    group,
    split,
    swap,
    trackTarget,
    trackAttributes,
    confidenceLevel,
    secondaryConfidenceLevel,
    filteredRowAverages,
    items,
    // subjects,
    filters,
    hashes,
    timestamp,
    update
  } = requestOptions

  // Get query block UID parameter
  const queryBlockUID = blockUID || state?.currentSchema?.get<string>('currentBlockUID') || null

  // Get current block
  const queryBlock = queryBlockUID ? state?.currentSchema?.getIn<BlockModel>(['blocks', queryBlockUID]) || null : null

  // Get query subject type
  const querySubjectType =
    subjectType ||
    queryBlock?.getSubjectType() ||
    ('none' === staticStore.dashboardType ? null : blockTypeToSubjectType(staticStore.dashboardType))

  // Get query preset parameter
  const queryPreset = preset || queryBlock?.get<string>('currentPreset') || 'All'

  // Get query items parameter
  const queryItems = items || queryBlock?.getItemsArray() || null

  // Collect query wave uids
  const queryWaveUids = presetWaveUids(state, queryPreset, queryItems)

  // Get query split parameter
  const querySplit = split || queryBlock?.get<string>('currentSplit') || null

  // Get query swap parameter
  const querySwap = querySplit ? swap || queryBlock?.get<boolean>('swap') || false : false

  // Get query group parameter
  const queryGroup =
    SUBJECT_TYPES.CONCEPT === querySubjectType
      ? 'subjects'
      : group || queryBlock?.get<string>('currentWave') || 'asFielded'

  // Get query track target
  const queryTrackTarget = trackTarget || queryBlock?.get<TrackTargetType>('trackTarget') || null

  // Get query track attributes
  const queryTrackAttributes =
    trackAttributes || queryBlock?.get<Record<TrackTargetType, string | null>>('trackAttributes') || null

  // Get query options
  const queryOptions = {
    target: queryTrackTarget,
    attribute: queryTrackAttributes?.[queryTrackTarget] || null
  }

  // Get query confidence level
  const queryConfidenceLevel = confidenceLevel || queryBlock?.get<number>('confidenceLevel') || null

  // Get query secondary confidence level
  const querySecondaryConfidenceLevel =
    secondaryConfidenceLevel || queryBlock?.get<number>('secondaryConfidenceLevel') || null

  // Get query filtered row averages
  const queryFilteredRowAverages = filteredRowAverages || queryBlock?.get<boolean>('filteredRowAverages')

  // Get query filters
  const queryFilters = filters || getAllFilters(state)

  // Get combined query parameters
  const params = {
    type,
    subjectType: querySubjectType,
    blockUID: queryBlockUID,
    options: queryOptions,
    group: queryGroup,
    split: querySplit,
    swap: querySwap,
    confidenceLevel: queryConfidenceLevel,
    secondaryConfidenceLevel: querySecondaryConfidenceLevel,
    filteredRowAverages: queryFilteredRowAverages,
    filters: queryFilters,
    waveUids: queryWaveUids,
    hashes
  } as QueryFactoryType

  // Get export parameters
  if ('export' === type) {
    const { shiftKey, ctrlKey, format, metadata, linkToPortal } = requestOptions
    Object.assign(params, {
      shiftKey,
      ctrlKey,
      format,
      metadata,
      linkToPortal,
      title: state?.title,
      schema: { ...queryBlock?.toJS(), items: queryItems },
      items: queryItems,
      preset: queryPreset
    })
  }

  // Get query update parameters
  if ('update' === type) {
    const queryUpdate = update || getSubjectUpdatePayload(state) || null
    if (queryUpdate) {
      params.update = queryUpdate
    }
  }

  // Get query factory
  const factory = getQueryFactory(querySubjectType)

  // Get query
  const query = factory(params)

  // Get query refresh
  const queryRefresh = SYNC_REQUEST_TYPES.includes(type) ? 'noCache' : refresh

  // Get query sync
  const querySync = SYNC_REQUEST_TYPES.includes(type) ? true : sync

  // Prepare the request object
  const result = { query, refresh: queryRefresh, sync: querySync } as AppRequestType
  if (timestamp) {
    result.timestamp = timestamp
  }

  // Return the request object
  return result
}
